import { useI18n } from 'gunsi-auth'

const translations = {
  en: {
    nav: {
      overview: 'Overview',
      transactions: 'Transactions',
      categories: 'Categories',
      stats: 'Stats',
      converter: 'Converter',
      manage: 'Manage',
      settings: 'Settings',
      more: 'More',
    },
    sync: {
      offline: 'Offline',
      pending: 'to sync',
      syncing: 'Syncing…',
    },
    manage: {
      title: 'Manage names',
      sub: 'Edit, merge, or strip names across all your transactions.',
      search: 'Search name…',
      colName: 'Name',
      colCount: '#',
      colTotal: 'Total',
      colLast: 'Last',
      empty: 'No names yet.',
      noResults: 'No matches.',
      selectedN: '{n} selected',
      mergeInto: 'Merge into…',
      mergePrompt: 'Target name for {n} entries:',
      clearName: 'Strip name',
      cancel: 'Cancel',
      apply: 'Apply',
      confirmRename: 'Rename {from} to {to}?',
      confirmRenameSub: '{n} transactions will be updated.',
      confirmMerge: 'Merge {fromCount} names into {to}?',
      confirmMergeSub: '{n} transactions will be updated. Sources: {list}',
      confirmClear: 'Strip name from {n} transactions?',
      confirmClearSub: 'Affected names: {list}. The transactions themselves are not deleted.',
      doneRenamed: 'Renamed {n} transactions.',
      doneCleared: 'Stripped name from {n} transactions.',
    },
    converter: {
      title: 'Currency converter',
      from: 'From',
      to: 'To',
      amount: 'Amount',
      amountPlaceholder: 'Enter amount',
      result: 'Result',
      noRate: 'No rate available',
      historyTitle: 'History (rate over time)',
      historyEmpty: 'No history available for this pair.',
      swap: 'Swap',
      ranges: { '1W': '1W', '1M': '1M', '3M': '3M', '6M': '6M', '1Y': '1Y', '3Y': '3Y', '5Y': '5Y', 'ALL': 'All' },
    },
    home: {
      sub: 'Track every coin.',
      cta: 'Sign in',
    },
    dashboard: {
      title: 'Overview',
      income: 'Income',
      expenses: 'Expenses',
      net: 'Total',
      thisMonth: 'This month',
      empty: 'No transactions yet.',
      emptyHint: 'Add your first transaction to start tracking.',
    },
    overview: {
      soFar: 'So far',
      saved: 'saved since the 1st',
      monthEnd: 'Month end',
      estimate: 'if you keep this up',
      curveTitle: 'The month so far',
      curveSub: 'Balance day by day: income − expenses',
      legendActual: 'so far',
      legendProj: 'projection',
      legendEvent: 'scheduled payments',
      paceTitle: 'Your pace',
      paceSub: 'You spend ≈ {spend} a day · ≈ {income} a day comes in',
      paceSubNoIncome: 'You spend ≈ {spend} a day · no income recorded this month',
      paceOver: 'You’re spending {pct}% more than comes in',
      paceUnder: 'You’re keeping {pct}% of what comes in',
      projection: 'How it adds up',
      recordedIncome: 'Income this month',
      recordedExpenses: 'Expenses this month (scheduled ones too)',
      cashNote: 'Same numbers as Transactions: everything counts on its effective date, a flight in the month of the trip.',
      monthResult: 'Month result',
      yearResult: 'Year result',
      resultSub: 'income − expenses',
      savedYear: 'saved since the start of the year',
      perMonth: 'Average month',
      perMonthSub: 'income − expenses, per month',
      curveMonth: 'The month',
      curveYear: 'The year',
      curveYearSoFar: 'The year so far',
      balanceTitle: 'The balance',
      noIncome: 'No income recorded',
      balanceOver: 'You spent {pct}% more than came in',
      balanceUnder: 'You kept {pct}% of what came in',
      income: 'Income',
      paidExpenses: 'Expenses',
      result: 'Result',
      estimatedDaily: 'Estimated everyday spending',
      estimatedDailyDetail: '≈ {pace}/day × {days} days left',
      paceNote: 'Everyday spending = median of your last 4 weeks ÷ 7, so flights and one-off expenses don’t inflate it.',
      noPace: 'At least 2 weeks of transactions are needed to estimate the rest of the month: for now only what’s already recorded counts.',
      lastDay: 'Last day of the month: only what’s already recorded counts.',
      dayOf: 'Day {day} of {total}',
    },
    transactions: {
      title: 'Transactions',
      perDay: '/day',
      dailyAvgHint: 'Daily average',
      monthTotalsHint: 'Month totals',
      add: 'New transaction',
      edit: 'Edit transaction',
      empty: 'No transactions yet.',
      types: { income: 'Income', expense: 'Expense' },
      amount: 'Amount',
      currency: 'Currency',
      date: 'Date',
      paymentDate: 'Payment date',
      effectiveDate: 'Effective date',
      showOnDifferentDate: 'Show on a different date?',
      removeEffectiveDate: 'Remove effective date',
      split: 'Split',
      repeat: 'Repeat',
      splitToggleQuestion: 'Distribute across multiple dates?',
      splitCount: 'times',
      splitInterval: 'every',
      splitUnit: {
        days:        { one: 'day',         other: 'days' },
        weeks:       { one: 'week',        other: 'weeks' },
        months:      { one: 'month',       other: 'months' },        // non-Lil mode
        years:       { one: 'year',        other: 'years' },         // non-Lil mode
        months_greg: { one: 'greg. month', other: 'greg. months' },
        months_lil:  { one: 'lil month',   other: 'lil months' },
        years_greg:  { one: 'greg. year',  other: 'greg. years' },
        years_lil:   { one: 'lil year',    other: 'lil years' },
      },
      splitPreviewAmount: '{amount} each',
      splitPreviewTotal: 'total {total}',
      splitPreviewRange: '{from} → {to}',
      splitBadge: '{idx}/{total}',
      deleteAllSlicesConfirm: 'Delete entire split ({n} occurrences)?',
      thisMonth: 'This month',
      today: 'Today',
      now: 'Now',
      time: 'Time',
      category: 'Category',
      name: 'Name',
      namePlaceholder: 'Short title (optional)',
      notes: 'Notes',
      notesPlaceholder: 'Additional notes (optional)',
      deleteConfirm: 'Delete this transaction?',
      deleteConfirmSub: 'This cannot be undone.',
      copy: 'Copy',
      copyConfirm: 'Copy transaction',
      copyConfirmSub: 'Pick which fields to copy. A new transaction will be created.',
    },
    categories: {
      title: 'Categories',
      add: 'Add category',
      edit: 'Edit category',
      empty: 'No categories yet.',
      tabIncome: 'Income',
      tabExpense: 'Expense',
      namePlaceholder: 'Category name',
      parent: 'Parent',
      parentNone: 'None (top-level)',
      deleteConfirm: 'Confirm delete',
    },
    stats: {
      title: 'Stats',
      empty: 'Nothing to chart yet.',
      monthly: 'Monthly',
      yearly: 'Yearly',
      all: 'All',
      custom: 'Custom',
      misc: 'Miscellaneous',
      perDay: '/day',
      dailyAvgHint: 'Daily average',
      periodTotalsHint: 'Period totals',
    },
    detail: {
      all: 'All',
    },
    settings: {
      title: 'Settings',
      appearance: 'Appearance',
      theme: 'Theme',
      colorSet: 'Color set',
      currency: 'Default currency',
      calendar: 'Calendar',
      calendars: { gregorian: 'Gregorian', lil: 'Lil' },
      yearEra: 'Year notation',
      yearEras: { lil: 'Lil (L10210)', he: 'Holocene Era (HE12026)' },
      dayCalendar: 'Day labels',
      dayCalendars: { lil: 'Lil', gregorian: 'Gregorian' },
      dateFormat: 'Date format',
      dateFormats: {
        auto: 'Automatic (follows the language)',
        dmy: 'DD/MM/YYYY',
        mdy: 'MM/DD/YYYY',
        ymd: 'YYYY-MM-DD',
      },
      language: 'Language',
      data: 'Data',
      exportJson: 'Export as JSON',
      importJson: 'Import from JSON',
      reset: 'Reset all data',
      resetTitle: 'Reset all data?',
      resetWarning: 'This will permanently delete all your accounts, categories and transactions stored on this device. This cannot be undone.',
      resetContinue: 'Continue',
      resetTypePrompt: 'Type {phrase} to confirm:',
      resetConfirmPhrase: 'DELETE EVERYTHING',
      resetFinal: 'Delete everything',
      themes: { system: 'System', light: 'Light', dark: 'Dark' },
      colorSets: { modern: 'Modern', classic: 'Classic' },
      ledger: {
        title: 'Link to Ledger',
        toggle: 'Sync confirmed Ledger quotas',
        hint: 'Each quota you confirm in Ledger appears here as an expense, with its quota date. Unconfirming removes the matching transaction.',
        category: 'Category',
        categoryNone: 'None',
        nameTemplate: 'Transaction name',
        templateHint: 'Use {placeholder} for the period label (e.g. "March 2026" or "III").',
        backfilling: 'Importing…',
        wiping: 'Removing…',
        done: 'Done.',
      },
    },
    common: {
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      close: 'Close',
      choose: 'Choose',
      none: 'None',
      searchIcon: 'Search icon',
      searchCurrency: 'Search currency, country, symbol…',
      searchCategory: 'Search category…',
    },
  },
  it: {
    nav: {
      overview: 'Panoramica',
      transactions: 'Movimenti',
      categories: 'Categorie',
      stats: 'Statistiche',
      converter: 'Convertitore',
      manage: 'Gestisci',
      settings: 'Impostazioni',
      more: 'Altro',
    },
    sync: {
      offline: 'Offline',
      pending: 'da sincronizzare',
      syncing: 'Sincronizzo…',
    },
    manage: {
      title: 'Gestisci nomi',
      sub: 'Modifica, accorpa o rimuovi i nomi su tutti i movimenti.',
      search: 'Cerca nome…',
      colName: 'Nome',
      colCount: '#',
      colTotal: 'Totale',
      colLast: 'Ultimo',
      empty: 'Nessun nome.',
      noResults: 'Nessuna corrispondenza.',
      selectedN: '{n} selezionati',
      mergeInto: 'Accorpa in…',
      mergePrompt: 'Nome di destinazione per {n} voci:',
      clearName: 'Rimuovi nome',
      cancel: 'Annulla',
      apply: 'Applica',
      confirmRename: 'Rinominare {from} in {to}?',
      confirmRenameSub: 'Saranno aggiornati {n} movimenti.',
      confirmMerge: 'Accorpare {fromCount} nomi in {to}?',
      confirmMergeSub: 'Saranno aggiornati {n} movimenti. Origini: {list}',
      confirmClear: 'Rimuovere il nome da {n} movimenti?',
      confirmClearSub: 'Nomi interessati: {list}. I movimenti non vengono cancellati.',
      doneRenamed: 'Rinominati {n} movimenti.',
      doneCleared: 'Rimosso il nome da {n} movimenti.',
    },
    converter: {
      title: 'Convertitore valuta',
      from: 'Da',
      to: 'A',
      amount: 'Importo',
      amountPlaceholder: 'Inserisci importo',
      result: 'Risultato',
      noRate: 'Cambio non disponibile',
      historyTitle: 'Storico (cambio nel tempo)',
      historyEmpty: 'Nessuno storico disponibile per questa coppia.',
      swap: 'Inverti',
      ranges: { '1W': '1S', '1M': '1M', '3M': '3M', '6M': '6M', '1Y': '1A', '3Y': '3A', '5Y': '5A', 'ALL': 'Tutto' },
    },
    home: {
      sub: 'Conta ogni moneta.',
      cta: 'Accedi',
    },
    dashboard: {
      title: 'Panoramica',
      income: 'Entrate',
      expenses: 'Uscite',
      net: 'Totale',
      thisMonth: 'Questo mese',
      empty: 'Nessun movimento.',
      emptyHint: 'Aggiungi il primo movimento per iniziare.',
    },
    overview: {
      soFar: 'Finora',
      saved: 'risparmiati dal 1°',
      monthEnd: 'Fine mese',
      estimate: 'se continui così',
      curveTitle: 'Il mese finora',
      curveSub: 'Saldo giorno per giorno: entrate − uscite',
      legendActual: 'finora',
      legendProj: 'proiezione',
      legendEvent: 'pagamenti programmati',
      paceTitle: 'Il tuo ritmo',
      paceSub: 'Spendi ≈ {spend} al giorno · ne entrano ≈ {income}',
      paceSubNoIncome: 'Spendi ≈ {spend} al giorno · nessuna entrata registrata nel mese',
      paceOver: 'Stai spendendo il {pct}% in più di quanto entra',
      paceUnder: 'Stai tenendo da parte il {pct}% di quanto entra',
      projection: 'Come si arriva al totale',
      recordedIncome: 'Entrate del mese',
      recordedExpenses: 'Uscite del mese (anche quelle programmate)',
      cashNote: 'Stessi numeri di Movimenti: ogni movimento conta alla sua data effettiva, un volo nel mese del viaggio.',
      monthResult: 'Risultato del mese',
      yearResult: 'Risultato dell’anno',
      resultSub: 'entrate − uscite',
      savedYear: 'risparmiati da inizio anno',
      perMonth: 'Mese medio',
      perMonthSub: 'entrate − uscite, al mese',
      curveMonth: 'Il mese',
      curveYear: 'L’anno',
      curveYearSoFar: 'L’anno finora',
      balanceTitle: 'Il bilancio',
      noIncome: 'Nessuna entrata registrata',
      balanceOver: 'Hai speso il {pct}% in più di quanto è entrato',
      balanceUnder: 'Hai tenuto da parte il {pct}% di quanto è entrato',
      income: 'Entrate',
      paidExpenses: 'Uscite',
      result: 'Risultato',
      estimatedDaily: 'Spese quotidiane stimate',
      estimatedDailyDetail: '≈ {pace}/giorno × {days} giorni rimasti',
      paceNote: 'Spese quotidiane = mediana delle ultime 4 settimane ÷ 7: voli e spese eccezionali non la gonfiano.',
      noPace: 'Servono almeno 2 settimane di movimenti per stimare il resto del mese: per ora conta solo ciò che è già registrato.',
      lastDay: 'Ultimo giorno del mese: conta solo ciò che è già registrato.',
      dayOf: 'Giorno {day} di {total}',
    },
    transactions: {
      title: 'Movimenti',
      perDay: '/g',
      dailyAvgHint: 'Media giornaliera',
      monthTotalsHint: 'Totali del mese',
      add: 'Nuovo movimento',
      edit: 'Modifica movimento',
      empty: 'Nessun movimento.',
      types: { income: 'Entrata', expense: 'Uscita' },
      amount: 'Importo',
      currency: 'Valuta',
      date: 'Data',
      paymentDate: 'Data pagamento',
      effectiveDate: 'Data effettiva',
      showOnDifferentDate: 'È da mostrare su un’altra data?',
      removeEffectiveDate: 'Rimuovi data effettiva',
      split: 'Suddividi',
      repeat: 'Ripeti',
      splitToggleQuestion: 'Distribuisci su più date?',
      splitCount: 'volte',
      splitInterval: 'ogni',
      splitUnit: {
        days:        { one: 'giorno',     other: 'giorni' },
        weeks:       { one: 'settimana',  other: 'settimane' },
        months:      { one: 'mese',       other: 'mesi' },         // non-Lil mode
        years:       { one: 'anno',       other: 'anni' },         // non-Lil mode
        months_greg: { one: 'mese greg.', other: 'mesi greg.' },
        months_lil:  { one: 'mese lil',   other: 'mesi lil' },
        years_greg:  { one: 'anno greg.', other: 'anni greg.' },
        years_lil:   { one: 'anno lil',   other: 'anni lil' },
      },
      splitPreviewAmount: '{amount}/volta',
      splitPreviewTotal: 'totale {total}',
      splitPreviewRange: '{from} → {to}',
      splitBadge: '{idx}/{total}',
      deleteAllSlicesConfirm: 'Eliminare tutta la suddivisione ({n} occorrenze)?',
      thisMonth: 'Questo mese',
      today: 'Oggi',
      now: 'Adesso',
      time: 'Ora',
      category: 'Categoria',
      name: 'Nome',
      namePlaceholder: 'Titolo breve (opzionale)',
      notes: 'Note',
      notesPlaceholder: 'Note aggiuntive (opzionali)',
      deleteConfirm: 'Eliminare questo movimento?',
      deleteConfirmSub: 'L’operazione non è reversibile.',
      copy: 'Copia',
      copyConfirm: 'Copia movimento',
      copyConfirmSub: 'Scegli i campi da copiare. Verrà creato un nuovo movimento.',
    },
    categories: {
      title: 'Categorie',
      add: 'Aggiungi categoria',
      edit: 'Modifica categoria',
      empty: 'Nessuna categoria.',
      tabIncome: 'Entrate',
      tabExpense: 'Uscite',
      namePlaceholder: 'Nome categoria',
      parent: 'Padre',
      parentNone: 'Nessuna (livello principale)',
      deleteConfirm: 'Conferma eliminazione',
    },
    stats: {
      title: 'Statistiche',
      empty: 'Niente da mostrare.',
      monthly: 'Mensile',
      yearly: 'Annuale',
      all: 'Tutto',
      custom: 'Intervallo',
      misc: 'Varie',
      perDay: '/g',
      dailyAvgHint: 'Media giornaliera',
      periodTotalsHint: 'Totali del periodo',
    },
    detail: {
      all: 'Tutto',
    },
    settings: {
      title: 'Impostazioni',
      appearance: 'Aspetto',
      theme: 'Tema',
      colorSet: 'Palette',
      currency: 'Valuta predefinita',
      calendar: 'Calendario',
      calendars: { gregorian: 'Gregoriano', lil: 'Lil' },
      yearEra: 'Notazione anno',
      yearEras: { lil: 'Lil (L10210)', he: 'Olocenico (HE12026)' },
      dayCalendar: 'Etichetta giorni',
      dayCalendars: { lil: 'Lil', gregorian: 'Gregoriano' },
      dateFormat: 'Formato data',
      dateFormats: {
        auto: 'Automatico (segue la lingua)',
        dmy: 'GG/MM/AAAA',
        mdy: 'MM/GG/AAAA',
        ymd: 'AAAA-MM-GG',
      },
      language: 'Lingua',
      data: 'Dati',
      exportJson: 'Esporta JSON',
      importJson: 'Importa JSON',
      reset: 'Reset di tutti i dati',
      resetTitle: 'Cancellare tutti i dati?',
      resetWarning: 'Questa operazione cancellerà definitivamente tutti i conti, le categorie e i movimenti memorizzati su questo dispositivo. Operazione irreversibile.',
      resetContinue: 'Continua',
      resetTypePrompt: 'Scrivi {phrase} per confermare:',
      resetConfirmPhrase: 'ELIMINA TUTTO',
      resetFinal: 'Elimina tutto',
      themes: { system: 'Sistema', light: 'Chiaro', dark: 'Scuro' },
      colorSets: { modern: 'Moderno', classic: 'Classico' },
      ledger: {
        title: 'Collega Ledger',
        toggle: 'Sincronizza le quote confermate di Ledger',
        hint: 'Ogni quota confermata in Ledger appare qui come uscita, con la data della quota. Annullare la conferma elimina il movimento.',
        category: 'Categoria',
        categoryNone: 'Nessuna',
        nameTemplate: 'Nome del movimento',
        templateHint: 'Usa {placeholder} per l’etichetta del periodo (es. "Marzo 2026" oppure "III").',
        backfilling: 'Importazione…',
        wiping: 'Rimozione…',
        done: 'Fatto.',
      },
    },
    common: {
      cancel: 'Annulla',
      save: 'Salva',
      delete: 'Elimina',
      edit: 'Modifica',
      close: 'Chiudi',
      choose: 'Scegli',
      none: 'Nessuna',
      searchIcon: 'Cerca icona',
      searchCurrency: 'Cerca valuta, paese, simbolo…',
      searchCategory: 'Cerca categoria…',
    },
  },
}

export function useTallyI18n() {
  const { locale } = useI18n()
  const lang = locale?.startsWith('it') ? 'it' : 'en'
  return translations[lang]
}
